/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/lineage.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * `downstream` is impact — what breaks if this changes. `upstream` is provenance — what produced
 * this.
 */
export type Direction = 'downstream' | 'upstream';

export const DIRECTIONS: readonly Direction[] = ['downstream', 'upstream'];

export function isDirection(value: unknown): value is Direction {
  return (['downstream', 'upstream'] as readonly unknown[]).includes(value);
}

/**
 * A **job is a node**, not a label on an edge. "Which job wrote this" is the first question anyone
 * asks after "what broke", and a job that exists only as an edge attribute cannot be listed, owned
 * or asked about. It also makes the common shape honest: a transformation reading two tables and
 * writing one is `dataset → job → dataset`, which is what OpenLineage reports, rather than a
 * fabricated dataset-to-dataset edge attributing the work to nobody. A `column` node is a column
 * of a dataset *version*, because which columns exist is a property of the version — a column
 * keyed on the dataset alone would survive a rename it should not.
 */
export type NodeKind = 'dataset' | 'column' | 'job';

export const NODE_KINDS: readonly NodeKind[] = ['dataset', 'column', 'job'];

export function isNodeKind(value: unknown): value is NodeKind {
  return (['dataset', 'column', 'job'] as readonly unknown[]).includes(value);
}

/**
 * Where the assertion came from, in descending order of authority. On the record for ever, so an
 * auditor can ask years later which edges a model invented. * `openlineage_facet` — the producing
 * job declared it. Fact. * `sql_parse` — derived by parsing the transformation's SQL. Fact,
 * subject to the parser being right about the dialect, which is why it stays distinguishable from
 * a facet. **No adapter ships**: sqlglot is not installed. * `llm_inference` — a model's guess
 * from names and types. **Never fact.** Above the floor it is still a guess and the source says
 * so; at or below it, `needs_review`. **No adapter ships**: no validator-gated local model is
 * wired. * `manual` — a person asserted it. Fact, and attributable: `asserted_by` is always
 * present.
 */
export type EdgeSource = 'openlineage_facet' | 'sql_parse' | 'llm_inference' | 'manual';

export const EDGE_SOURCES: readonly EdgeSource[] = ['openlineage_facet', 'sql_parse', 'llm_inference', 'manual'];

export function isEdgeSource(value: unknown): value is EdgeSource {
  return (['openlineage_facet', 'sql_parse', 'llm_inference', 'manual'] as readonly unknown[]).includes(value);
}

/**
 * Derived from `source` and `confidence`, not set independently. `needs_review` is **excluded from
 * impact and provenance**, and that exclusion is the point: a blast radius padded with unconfirmed
 * guesses trains people to ignore it.
 */
export type EdgeState = 'active' | 'needs_review' | 'rejected';

export const EDGE_STATES: readonly EdgeState[] = ['active', 'needs_review', 'rejected'];

export function isEdgeState(value: unknown): value is EdgeState {
  return (['active', 'needs_review', 'rejected'] as readonly unknown[]).includes(value);
}

/**
 * Which lens a lineage read is shown through: fetched directly from the source platform's own
 * lineage catalogue, or recorded by Lumiq some other way — never blended without saying so. **A
 * provenance-*channel* distinction, deliberately not `EdgeSource`.** `EdgeSource` answers "is this
 * a stated fact or a guess" (D32); this answers "which door did it come through". A native harvest
 * reading Snowflake's `ACCOUNT_USAGE` or Databricks Unity Catalog's `system.access.*`, and a
 * genuine OpenLineage-instrumented pipeline posting to `POST /v1/lineage/events`, are **both**
 * `openlineage_facet` — same standing, same trust — so a source-based split would answer "how much
 * do we trust this" when the question is "did the source platform say so directly, or did Lumiq
 * assemble this". `native` is every edge a native harvest produced; `platform` is everything else
 * Lumiq holds — a producer's own pushed event today, and, once a SQL-lineage parser or a
 * validator-gated inference module exists, whatever they derive. **`platform` is not empty on
 * every deployment**, unlike a source-based split would make it: a real OpenLineage-instrumented
 * pipeline is `platform` under this lens, because the source platform's own catalogue was never
 * asked for that flow. It reports genuinely empty only for a subgraph whose entire chain came from
 * native harvests, and never silently widens to `native`'s answer. `combined` applies no filter
 * and is exactly the graph this route has always returned.
 */
export type LineageLens = 'native' | 'platform' | 'combined';

export const LINEAGE_LENSES: readonly LineageLens[] = ['native', 'platform', 'combined'];

export function isLineageLens(value: unknown): value is LineageLens {
  return (['native', 'platform', 'combined'] as readonly unknown[]).includes(value);
}

/**
 * Why the closure was not extended through an edge. `row_bound` — the projected row count exceeded
 * the per-edge limit. `cycle` — the edge would close a loop, and a closure over a cycle does not
 * terminate.
 */
export type RefusalReason = 'row_bound' | 'cycle';

export const REFUSAL_REASONS: readonly RefusalReason[] = ['row_bound', 'cycle'];

export function isRefusalReason(value: unknown): value is RefusalReason {
  return (['row_bound', 'cycle'] as readonly unknown[]).includes(value);
}

/**
 * Why an answer is not the whole picture. **The distinction is the reason this field exists.**
 * `restricted` means the caller may not see part of the graph — ask a data owner. `truncated`
 * means the walk hit its depth bound — widen the query. Different owners, different fixes, and a
 * caller handed a bare "incomplete" chases the wrong one.
 */
export type IncompletenessReason = 'restricted' | 'truncated';

export const INCOMPLETENESS_REASONS: readonly IncompletenessReason[] = ['restricted', 'truncated'];

export function isIncompletenessReason(value: unknown): value is IncompletenessReason {
  return (['restricted', 'truncated'] as readonly unknown[]).includes(value);
}

/**
 * `partial` is the state that matters: the graph was updated and something was refused, so it is
 * neither a success to move on from nor a failure to retry. It is the same distinction
 * `HarvestState.PARTIAL` makes, for the same reason.
 */
export type IngestState = 'pending' | 'running' | 'succeeded' | 'partial' | 'failed';

export const INGEST_STATES: readonly IngestState[] = ['pending', 'running', 'succeeded', 'partial', 'failed'];

export function isIngestState(value: unknown): value is IngestState {
  return (['pending', 'running', 'succeeded', 'partial', 'failed'] as readonly unknown[]).includes(value);
}

/**
 * Which registered connection owns the dataset behind a node. A dataset's identity in this
 * platform is `(connection, schema, object)`, so two connections holding `ANALYTICS.CUSTOMER` — a
 * prod and a dev Snowflake, or a Snowflake and a Databricks table of the same name — are different
 * assets. Without this a graph draws them as two boxes a reader cannot tell apart, and lineage is
 * a *unified* graph precisely so that a blast radius crossing from one system into another is
 * visible. Carried on the node rather than resolved by a client, for the reason `display_name` is:
 * resolving it per node is one request per box against an endpoint whose purpose is answering in a
 * single round trip. **There is deliberately no `environment` field.** `catalog.connection` has no
 * such column, so one here could only be invented — and an environment badge that reads
 * `production` because a screen guessed is worse than no badge.
 */
export interface ConnectionRef {
  readonly id: string;
  /** The name an operator registered the connection under. */
  readonly name: string;
  /**
   * The connector type — `snowflake`, `databricks`, `postgresql`, and so on. A string rather than
   * an enum on purpose: it is a label a screen renders beside the connection's name, and nothing
   * in `core.lineage` branches on it. A new connector type must not require a contract change here
   * to appear in a graph.
   */
  readonly platform: string;
}

export function isConnectionRef(value: unknown): value is ConnectionRef {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['name'] === 'string' &&
    typeof candidate['platform'] === 'string'
  );
}

export interface Node {
  readonly id: string;
  readonly kind: NodeKind;
  /**
   * The producer's own name for it — an OpenLineage dataset name, a job namespace and name. Ingest
   * is idempotent on this: the same run event arriving twice finds the node it created the first
   * time.
   */
  readonly external_id: string;
  readonly display_name: string;
  /**
   * The `core.catalog` dataset this node stands for, or `null`. Null for a job, and for a dataset
   * no harvest has catalogued yet — see `is_catalogued`.
   */
  readonly dataset_id?: string | null;
  /** Present only on a `column` node. */
  readonly column_name?: string | null;
  /**
   * Whether Lumiq knows this node as a catalogued dataset. Carried explicitly rather than left for
   * a client to infer from a null `dataset_id`, because the two reasons for that null are
   * different: a job is never going to be catalogued, and a dataset is one harvest away.
   */
  readonly is_catalogued: boolean;
  /**
   * Which connection owns this node, or `null` when there is none to name — a job belongs to no
   * connection, and a dataset a producer named before any harvest saw it has none yet. Those are
   * the two states `is_catalogued` distinguishes, so this adds no third meaning.
   */
  readonly connection?: ConnectionRef | null;
}

export function isNode(value: unknown): value is Node {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    (['dataset', 'column', 'job'] as readonly unknown[]).includes(candidate['kind']) &&
    typeof candidate['external_id'] === 'string' &&
    typeof candidate['display_name'] === 'string' &&
    typeof candidate['is_catalogued'] === 'boolean'
  );
}

export interface NodeReach {
  readonly node_id: string;
  /**
   * Distinct nodes that reach this one, within `depth_limit`. A measured `0` — this node has no
   * recorded provenance — never an absence.
   */
  readonly upstream: number;
  /** Distinct nodes this one reaches, within `depth_limit`. A measured `0`. */
  readonly downstream: number;
  /**
   * The hop bound the counts were taken at, which equals `MAX_IMPACT_DEPTH`. On the wire rather
   * than implicit because the counts are only interpretable against it: comparing one of these
   * against a walk run at a shallower depth is comparing two different questions, and nothing else
   * in the response would say so.
   */
  readonly depth_limit: number;
}

export function isNodeReach(value: unknown): value is NodeReach {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['node_id'] === 'string' &&
    typeof candidate['upstream'] === 'number' &&
    typeof candidate['downstream'] === 'number' &&
    typeof candidate['depth_limit'] === 'number'
  );
}

export interface Edge {
  readonly id: string;
  readonly upstream_id: string;
  /**
   * The upstream node's name. Required, like `AffectedNode.display_name` and for the same reason:
   * the inspection table rendered `upstream_id → downstream_id` as two UUIDs, and a reviewer
   * deciding whether an inferred edge is real cannot judge that from two UUIDs.
   */
  readonly upstream_name: string;
  readonly upstream_kind: NodeKind;
  readonly downstream_id: string;
  readonly downstream_name: string;
  readonly downstream_kind: NodeKind;
  readonly source: EdgeSource;
  readonly state: EdgeState;
  /**
   * Present **only** for `llm_inference`, and required there. A facet, a parse and a human
   * assertion are not probabilistic, and a number on one of those would invite a screen to render
   * "0.94 confident" about something a pipeline simply stated.
   */
  readonly confidence?: number | null;
  /**
   * Who asserted it. Always present for `manual`: an unattributable human assertion is
   * indistinguishable from a fabricated one.
   */
  readonly asserted_by?: string | null;
  /**
   * The OpenLineage run that produced this assertion, when it came from one. Kept so "why does the
   * graph think this" is answerable by pointing at a specific run rather than an ingest date.
   */
  readonly run_id?: string | null;
  readonly valid_from: string;
  /**
   * The instant the edge stopped being true, or `null` while it still is. **Not** the last instant
   * it was true — the window is half-open, so an edge closed at T and one opened at T do not both
   * cover T.
   */
  readonly valid_to?: string | null;
  /**
   * Whether this edge contributes to impact and provenance. Derived, and carried so a screen need
   * not re-implement the rule — `false` for anything that is not `active`.
   */
  readonly is_traversable: boolean;
}

export function isEdge(value: unknown): value is Edge {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['upstream_id'] === 'string' &&
    typeof candidate['upstream_name'] === 'string' &&
    (['dataset', 'column', 'job'] as readonly unknown[]).includes(candidate['upstream_kind']) &&
    typeof candidate['downstream_id'] === 'string' &&
    typeof candidate['downstream_name'] === 'string' &&
    (['dataset', 'column', 'job'] as readonly unknown[]).includes(candidate['downstream_kind']) &&
    (['openlineage_facet', 'sql_parse', 'llm_inference', 'manual'] as readonly unknown[]).includes(candidate['source']) &&
    (['active', 'needs_review', 'rejected'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['valid_from'] === 'string' &&
    typeof candidate['is_traversable'] === 'boolean'
  );
}

export interface AffectedNode {
  readonly node_id: string;
  /**
   * The node's name, so a blast radius is readable. Required, not optional: the answer carried
   * `node_id` alone at first and the screen rendered a column of UUIDs, which during an incident
   * cannot distinguish a job from a dataset or a staging table from a mart. Resolving it
   * client-side would be one request per affected node against the endpoint whose purpose is
   * answering in a single round trip.
   */
  readonly display_name: string;
  /**
   * Whether the reached thing is a dataset, a column or a job. A job in a blast radius means "this
   * pipeline breaks"; a dataset means "this table goes stale". Different reactions.
   */
  readonly kind: NodeKind;
  /**
   * Hops from the subject along the **shortest** path. At least 1 — depth 0 would be the query's
   * own subject, and including it makes every blast radius look one larger.
   */
  readonly depth: number;
  /**
   * Whether any edge on the path came from inference. `false` for every path in this deployment,
   * because `needs_review` edges are not traversable. Present so that when a person promotes an
   * inference to fact, the resulting reachability stays distinguishable from a producer-declared
   * one.
   */
  readonly through_inferred: boolean;
  /**
   * Which connection owns this reached node, or `null` when there is none to name. The same
   * argument as `display_name` above, and the reason lineage is a unified graph rather than one
   * graph per connection: a blast radius that crosses from a Databricks silver table into a
   * Snowflake mart is the answer a reader most needs, and without this the two render identically.
   * `null` for a job and for a dataset no harvest has catalogued — neither belongs to a
   * connection, and a badge on either would be invented.
   */
  readonly connection?: ConnectionRef | null;
  /**
   * The catalogued dataset this reached node is, or `null` for a job and for a dataset no harvest
   * has catalogued. Carried so a graph can open a neighbour's dataset and show its quality without
   * one request per node — the same single-round-trip argument as `display_name` and `connection`.
   */
  readonly dataset_id?: string | null;
}

export function isAffectedNode(value: unknown): value is AffectedNode {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['node_id'] === 'string' &&
    typeof candidate['depth'] === 'number' &&
    typeof candidate['display_name'] === 'string' &&
    (['dataset', 'column', 'job'] as readonly unknown[]).includes(candidate['kind']) &&
    typeof candidate['through_inferred'] === 'boolean'
  );
}

export interface ImpactAnswer {
  readonly subject_id: string;
  readonly direction: Direction;
  /** The nodes this caller may see, nearest first. A node reached by several paths appears once. */
  readonly affected: readonly AffectedNode[];
  readonly visible_count: number;
  /**
   * **Required.** Whether this answer may be acted on as if it were the whole picture. There is no
   * response shape here that carries a blast radius without it, because the screen that forgets to
   * check is the one somebody makes a decision from.
   */
  readonly complete: boolean;
  /** Present when `complete` is `false`; absent otherwise. */
  readonly reason?: IncompletenessReason;
  /**
   * How many reachable nodes this caller may not see. **Present only for a principal holding the
   * tenant-wide lineage scope** — for anyone else an exact count would disclose the existence and
   * number of objects they have no rights over, and repeated across subjects it enumerates them.
   * Its absence is not a hint that nothing was withheld; `complete` and `reason` are what answer
   * that.
   */
  readonly withheld?: number;
  /** Whether a depth bound stopped the walk before it ran out of graph. */
  readonly truncated?: boolean;
  /** The instant the graph was read at. Always present, including when defaulted. */
  readonly as_of: string;
  /**
   * Which sources this walk admitted. Always echoed, for `as_of`'s reason: a caller that asked for
   * `native` and forgot which lens answered would misread an empty blast radius as "nothing
   * downstream" rather than "nothing natively-sourced downstream". `combined` when the caller did
   * not ask, which is the answer this route has always given.
   */
  readonly lens: LineageLens;
  /**
   * When the read model behind this response was last projected (ADR-009). **Null throughout this
   * module** — lineage has no CQRS read model, so every response here is a direct read, and null
   * means "current" rather than "unknown". Carried anyway: a client that special-cases its absence
   * for one module will get it wrong for the module that does have one.
   */
  readonly projected_at: string | null;
}

export function isImpactAnswer(value: unknown): value is ImpactAnswer {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['subject_id'] === 'string' &&
    (['downstream', 'upstream'] as readonly unknown[]).includes(candidate['direction']) &&
    Array.isArray(candidate['affected']) &&
    (candidate['affected'] as readonly unknown[]).every((item) => isAffectedNode(item)) &&
    typeof candidate['visible_count'] === 'number' &&
    typeof candidate['complete'] === 'boolean' &&
    typeof candidate['as_of'] === 'string' &&
    (['native', 'platform', 'combined'] as readonly unknown[]).includes(candidate['lens']) &&
    (typeof candidate['projected_at'] === 'string' || candidate['projected_at'] === null)
  );
}

export interface DatasetRef {
  /** The producer's name for the dataset, e.g. `postgres://warehouse/public.orders`. */
  readonly external_id: string;
  /** Falls back to `external_id` when omitted. */
  readonly display_name?: string;
}

export function isDatasetRef(value: unknown): value is DatasetRef {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['external_id'] === 'string'
  );
}

/**
 * One entry from a `columnLineage` facet. Produces `openlineage_facet` edges — the job declared
 * this, so it is fact and carries no confidence.
 */
export interface ColumnEdgeRef {
  readonly output_external_id: string;
  readonly output_column: string;
  readonly input_external_id: string;
  readonly input_column: string;
}

export function isColumnEdgeRef(value: unknown): value is ColumnEdgeRef {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['output_external_id'] === 'string' &&
    typeof candidate['output_column'] === 'string' &&
    typeof candidate['input_external_id'] === 'string' &&
    typeof candidate['input_column'] === 'string'
  );
}

export interface RunEvent {
  readonly run_id: string;
  /**
   * Required. An event with no job is an assertion nobody made, and a job node is what makes
   * "which job wrote this" answerable.
   */
  readonly job: Readonly<Record<string, unknown>>;
  readonly inputs?: readonly DatasetRef[];
  readonly outputs?: readonly DatasetRef[];
  /**
   * A `columnLineage` facet, if the producer emitted one. Absent is the common case and is not an
   * error — the result is table-level lineage, which is narrower rather than wrong.
   */
  readonly column_edges?: readonly ColumnEdgeRef[];
  /**
   * When the run happened. Defaults to the ingest time, and the distinction matters: a run event
   * replayed a week late asserts lineage as of when it ran, not as of now.
   */
  readonly event_time?: string;
}

export function isRunEvent(value: unknown): value is RunEvent {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['run_id'] === 'string' &&
    candidate['job'] !== undefined
  );
}

/**
 * An edge that was **stored but not expanded**. The assertion is kept; the closure is not extended
 * through it, so impact queries past `downstream_id` under-report from now on. That consequence is
 * the reason this is a response field and a published event rather than a log line.
 */
export interface Refusal {
  readonly reason: RefusalReason;
  readonly edge_id: string;
  readonly upstream_id: string;
  readonly downstream_id: string;
  /** What the expansion would have written. Present for `row_bound`. */
  readonly projected_rows?: number | null;
  /**
   * The per-edge ceiling that was exceeded. Carried alongside `projected_rows` because "refused"
   * without both cannot distinguish a pathological graph from a limit set too low.
   */
  readonly limit?: number | null;
  /**
   * Every node on the loop the edge would close. Non-empty for `cycle`, **empty** otherwise. An
   * empty array rather than `null`, and the reason is the client rather than the server: a
   * nullable array makes a caller check for null *and* for length before it can render anything,
   * and the two states mean the same thing here. `reason` is what distinguishes a cycle refusal
   * from a bound refusal; this field only ever carries the path.
   */
  readonly cycle_path?: readonly string[];
  readonly detail: string;
}

export function isRefusal(value: unknown): value is Refusal {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['row_bound', 'cycle'] as readonly unknown[]).includes(candidate['reason']) &&
    typeof candidate['edge_id'] === 'string' &&
    typeof candidate['upstream_id'] === 'string' &&
    typeof candidate['downstream_id'] === 'string' &&
    typeof candidate['detail'] === 'string'
  );
}

export interface IngestOperation {
  readonly id: string;
  readonly run_id: string;
  readonly state: IngestState;
  /**
   * Whether the graph now reflects everything this event asserted. `false` whenever `refusals` is
   * non-empty — carried explicitly so a client need not infer completeness from the length of an
   * array.
   */
  readonly complete: boolean;
  readonly nodes_seen?: number;
  /**
   * Zero for a replay, and that is success rather than a no-op to worry about: the lineage was
   * already asserted.
   */
  readonly edges_appended?: number;
  /**
   * Carried separately from `edges_appended` rather than netted. A run that closed four edges and
   * opened four is not an event that changed nothing.
   */
  readonly edges_closed?: number;
  readonly closure_rows_written?: number;
  readonly refusals?: readonly Refusal[];
  readonly started_at?: string | null;
  readonly finished_at?: string | null;
  /**
   * When the read model behind this response was last projected (ADR-009). **Null throughout this
   * module** — lineage has no CQRS read model, so every response here is a direct read, and null
   * means "current" rather than "unknown". Carried anyway: a client that special-cases its absence
   * for one module will get it wrong for the module that does have one.
   */
  readonly projected_at: string | null;
}

export function isIngestOperation(value: unknown): value is IngestOperation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['run_id'] === 'string' &&
    (['pending', 'running', 'succeeded', 'partial', 'failed'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['complete'] === 'boolean' &&
    (typeof candidate['projected_at'] === 'string' || candidate['projected_at'] === null)
  );
}

export interface NativeHarvestRequest {
  /**
   * The connection whose lineage catalogue to read. Not a dataset: a native harvest enumerates one
   * source's whole lineage in a handful of statements, exactly as a catalog harvest enumerates its
   * whole catalogue.
   */
  readonly connection_id: string;
  /**
   * How far back to ask the source. `null` takes the adapter's default lookback. Stored on the
   * operation, because a harvest reads a window and "why is this edge missing" otherwise has one
   * available answer - the source never recorded it - when the true one may be that it predates
   * what this harvest asked for.
   */
  readonly since?: string | null;
}

export function isNativeHarvestRequest(value: unknown): value is NativeHarvestRequest {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['connection_id'] === 'string'
  );
}

export interface NativeHarvestOperation {
  readonly id: string;
  readonly connection_id: string;
  /**
   * `partial` is its own state and is not a soft success: the graph was updated *and* something
   * was refused, so neither retrying nor moving on is right on its own. Collapsing it into either
   * neighbour is how a graph that under-reports starts looking finished.
   */
  readonly state: 'pending' | 'running' | 'succeeded' | 'partial' | 'failed';
  /**
   * Whether the source answered in full. **`null` means the harvest has not finished**, not
   * `false` - a boolean defaulted to `false` would report every in-flight harvest as having
   * returned a partial answer, which is a statement about the source made before it was asked.
   */
  readonly complete: boolean | null;
  /**
   * Which view set answered, e.g. `snowflake.account_usage`. Host-written by the adapter, so it
   * names the mechanism and can never carry a value a customer chose. `null` until the source has
   * answered.
   */
  readonly source_label: string | null;
  /** The lookback asked for, or `null` when the adapter's default was taken. */
  readonly window_start: string | null;
  /**
   * Why the source could not answer in full. Host-written. Present only on a harvest whose
   * `complete` is `false`.
   */
  readonly incomplete_reason: string | null;
  readonly table_edges_read: number;
  readonly column_edges_read: number;
  /**
   * Tables the source reported as loaded from outside the warehouse. Counted apart from
   * `table_edges_read` because they are a different fact - and because reporting only edges once
   * made a Snowflake harvest print "read 0 table edges" beside four ingested events, an internal
   * contradiction on the one line an operator reads.
   */
  readonly loads_read: number;
  readonly events_ingested: number;
  /**
   * Events whose content digest was already ingested, so nothing was written. **Separate from
   * `events_ingested` on purpose**: on a steady estate every harvest after the first is entirely
   * replays, and a caller shown only "0 edges appended" cannot tell that healthy steady state from
   * a connection that has stopped answering.
   */
  readonly events_replayed: number;
  readonly nodes_seen: number;
  readonly edges_appended: number;
  readonly closure_rows_written: number;
  /**
   * Each source object whose name would not resolve to a catalogued dataset, in this platform's
   * own words. Names rather than a count, because a caller told "3 refused" cannot act and one
   * told which names can - usually by harvesting the catalogue first.
   */
  readonly refusals: readonly string[];
  /** Why a failed harvest failed. Host-written, never a driver's own message. */
  readonly detail: string | null;
  readonly requested_by: string;
  readonly created_at: string;
  readonly started_at: string | null;
  /** Set exactly when the state is terminal. */
  readonly finished_at: string | null;
}

export function isNativeHarvestOperation(value: unknown): value is NativeHarvestOperation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['connection_id'] === 'string' &&
    (['pending', 'running', 'succeeded', 'partial', 'failed'] as readonly unknown[]).includes(candidate['state']) &&
    (typeof candidate['complete'] === 'boolean' || candidate['complete'] === null) &&
    (typeof candidate['source_label'] === 'string' || candidate['source_label'] === null) &&
    (typeof candidate['window_start'] === 'string' || candidate['window_start'] === null) &&
    (typeof candidate['incomplete_reason'] === 'string' || candidate['incomplete_reason'] === null) &&
    typeof candidate['table_edges_read'] === 'number' &&
    typeof candidate['column_edges_read'] === 'number' &&
    typeof candidate['loads_read'] === 'number' &&
    typeof candidate['events_ingested'] === 'number' &&
    typeof candidate['events_replayed'] === 'number' &&
    typeof candidate['nodes_seen'] === 'number' &&
    typeof candidate['edges_appended'] === 'number' &&
    typeof candidate['closure_rows_written'] === 'number' &&
    Array.isArray(candidate['refusals']) &&
    (candidate['refusals'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    (typeof candidate['detail'] === 'string' || candidate['detail'] === null) &&
    typeof candidate['requested_by'] === 'string' &&
    typeof candidate['created_at'] === 'string' &&
    (typeof candidate['started_at'] === 'string' || candidate['started_at'] === null) &&
    (typeof candidate['finished_at'] === 'string' || candidate['finished_at'] === null)
  );
}
